import { useCallback, useMemo } from "react";

import type { DispatchCoreKind, FieldKind } from "@storyboard/dispatch-view/fieldKinds";
import { enumValueFor, fieldKindFor } from "@storyboard/dispatch-view/fieldKinds";
import type { ResolvedDispatchView } from "@storyboard/dispatch-view/resolve";
import type { DispatchControl, DispatchField, DispatchViewDefinition } from "@storyboard/dispatch-view/view";
import { dispatchFieldCandidates, fieldGroup, isAccentField } from "@/components/Dispatch/columns";
import type { FieldOption } from "@/components/Dispatch/DispatchFieldPicker";
import type { RowMenuField } from "@/components/Dispatch/DispatchRowMenu";
import type { TagDefinition } from "@/types/driver";
import { type ConditionNaming, canConditionOn } from "./adhocConditions";

/**
 * A field the Filter menu offers a standing editor for because it has no column header to click.
 */
export type FieldFilter = { key: DispatchField; label: string; kind: FieldKind };

/**
 * The fields the Filter menu gives an editor of their own: those that narrow rows yet never show as a column, and
 * that no control of the view already narrows.
 *
 * A column's header is where a field is filtered; the accent fields (the acknowledgement layer) have none, so the
 * Filter menu carries theirs. A view whose controls already target the field (the built-in view's "Unacknowledged
 * only" toggle) has its means, and a second editor on the same field would let a toggle and a chip compose to no rows
 * while both look active.
 */
export function filterMenuFields(
  candidates: readonly DispatchField[],
  controls: readonly DispatchControl[],
): DispatchField[] {
  const targeted = new Set(controls.flatMap((control) => (control.field ? [control.field] : [])));
  return candidates.filter((field) => isAccentField(field) && canConditionOn(field) && !targeted.has(field));
}

/**
 * The fields the pickers, the row menu, and the Filter menu offer, from the registry's roles.
 *
 * Sort picks from every candidate, its column shown, hidden, or nonexistent; grouping from the candidates whose values
 * repeat across rows; the row menu narrows on what the row shows plus the accent fields, which show as the dot rather
 * than as a column. The accent fields that narrow also get an editor of their own in the Filter menu, on any view
 * whose controls do not already target them: a column's header is where a field is filtered, and these have none.
 * The acknowledgement layer is the one a dispatcher reaches for most, and it is never a column.
 */
export function useDispatchFieldOptions({
  definition,
  resolved,
  tags,
  tagsBySlug,
  naming,
  coreKinds,
}: {
  definition: DispatchViewDefinition;
  resolved: ResolvedDispatchView;
  tags: readonly TagDefinition[];
  tagsBySlug: ReadonlyMap<string, TagDefinition>;
  /** How the view labels a field, so the options read as the headers do. */
  naming: ConditionNaming;
  /** The core kinds the page offers; the task kinds are among them only while tasks are on. */
  coreKinds: readonly DispatchCoreKind[];
}): {
  sortOptions: FieldOption[];
  groupByOptions: FieldOption[];
  rowMenuFields: RowMenuField[];
  fieldFilters: FieldFilter[];
} {
  const candidates = useMemo(
    () => dispatchFieldCandidates(definition, tags, resolved.grain, coreKinds),
    [definition, tags, resolved.grain, coreKinds],
  );
  const fieldOption = useCallback(
    (field: DispatchField): FieldOption => ({ key: field, label: naming.labelFor(field), group: fieldGroup(field) }),
    [naming],
  );
  const sortOptions = useMemo(() => candidates.map(fieldOption), [candidates, fieldOption]);
  const groupByOptions = useMemo(() => candidates.filter(canConditionOn).map(fieldOption), [candidates, fieldOption]);
  // The row menu narrows on the values in front of the dispatcher: the columns showing, plus the accent fields.
  const { visibility } = resolved.layout;
  const rowMenuFields = useMemo<RowMenuField[]>(
    () =>
      candidates.flatMap((field) => {
        if ((visibility[field] !== true && !isAccentField(field)) || !canConditionOn(field)) return [];
        const kind = fieldKindFor(field, tagsBySlug);
        const valueLabel = (value: string) =>
          enumValueFor(kind, value)?.label ??
          (kind.valueType === "boolean" ? (value === "true" ? "Yes" : "No") : value);
        const valueOnly = kind.valueType === "boolean" && kind.values != null;
        return [{ key: field, label: naming.labelFor(field), valueLabel, valueOnly }];
      }),
    [candidates, visibility, tagsBySlug, naming],
  );
  const fieldFilters = useMemo<FieldFilter[]>(
    () =>
      filterMenuFields(candidates, definition.query.controls).map((field) => ({
        key: field,
        label: naming.labelFor(field),
        kind: fieldKindFor(field, tagsBySlug),
      })),
    [candidates, definition.query.controls, tagsBySlug, naming],
  );
  return { sortOptions, groupByOptions, rowMenuFields, fieldFilters };
}
